import React from 'react';
import { Form, Input, Row, Col, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { PhoneFormItem } from '@/components/ui/PhoneFormItem';
import type { ResolvedServiceRequiredUserInfo } from '@/utils/serviceBooking';

interface GuestDetailsProps {
  appointmentType: 'user' | 'anonymous';
  /** When set (service booking), overrides default required flags for guest fields. */
  requiredUserInfo?: ResolvedServiceRequiredUserInfo;
}

/** Guest name / phone / email fields for anonymous bookings; fills the row when a field is not required. */
export const GuestDetails: React.FC<GuestDetailsProps> = ({
  appointmentType,
  requiredUserInfo
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  if (appointmentType !== 'anonymous') {
    return null;
  }

  const requireName = requiredUserInfo?.name ?? true;
  const requirePhone = requiredUserInfo?.phone ?? true;
  const requireEmail = requiredUserInfo?.email ?? true;

  return (
    <Row gutter={[token.marginSM, 0]}>
      {requireName ? (
        <Col xs={24} sm={requirePhone ? 12 : 24}>
          <Form.Item
            label={t('appointmentComponents.create.guest.name')}
            name="anonymousName"
            normalize={(value) => (typeof value === 'string' ? value : value)}
            rules={[
              { required: true, message: t('appointmentComponents.create.guest.nameRequired') }
            ]}
          >
            <Input
              placeholder={t('appointmentComponents.create.guest.namePlaceholder')}
              size="middle"
            />
          </Form.Item>
        </Col>
      ) : null}
      {requirePhone ? (
        <Col xs={24} sm={requireName ? 12 : 24}>
          <PhoneFormItem
            label={t('appointmentComponents.create.guest.phone')}
            name="anonymousPhone"
            rules={[
              { required: true, message: t('appointmentComponents.create.guest.phoneRequired') },
            ]}
          />
        </Col>
      ) : null}
      {requireEmail ? (
        <Col span={24}>
          <Form.Item
            label={t('appointmentComponents.create.guest.email')}
            name="anonymousEmail"
            normalize={(value) => (typeof value === 'string' ? value.trim() : value)}
            rules={[
              { required: true, message: t('appointmentComponents.create.guest.emailRequired') },
              { type: 'email', message: t('appointmentComponents.create.guest.emailInvalid') }
            ]}
          >
            <Input
              placeholder={t('appointmentComponents.create.guest.emailPlaceholder')}
              size="middle"
              type="email"
            />
          </Form.Item>
        </Col>
      ) : null}
    </Row>
  );
};
